import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registrationType } from '../Constants/RegistrationType';
import { RetreatPageOptions } from '../Constants/RegistrationPageOptions';
import {
   INITIAL_PROMO_CODE, applyPromo, getRetreatConfig, getSonCount, getRoomSurcharge, getSonSurcharge,
   getRetreatCampFee, getRetreatTotal, showsAdditionalDetails, getDefaultRoomType
} from '../Constants/RetreatConfig';
import DataAccessHelper from '../Helper/DataAccessHelper';
import InputValidation from '../Helper/InputValidation';
import PersonalInfo from '../Components/PersonalInfo';
import CouplesRetreatPersonalInfo from '../Components/CouplesRetreatPersonalInfo';
import FatherSonPersonalInfo from '../Components/FatherSonPersonalInfo';
import RetreatWeekSelection from '../Components/RetreatWeekSelection';
import MedicalInsuranceInformation from '../Components/MedicalInsuranceInformation';
import RegistrationAdditionalDetails from '../Components/RegistrationAdditionalDetails';
import RetreatJoinGroup from '../Components/RetreatJoinGroup';
import Disclaimer from '../Components/Disclaimer';
import RetreatRegistrationReview from '../Components/RetreatRegistrationReview';
import IndividualRegistrationPayment from '../Components/IndividualRegistrationPayment';
import { useCart } from '../Contexts/CartContext';
import '../Styles/RetreatRegistration.scss';

function RetreatRegistration() {

   const navigate = useNavigate();
   const { refreshCount } = useCart();
   const [account, setAccount] = useState();
   const [selectedPage, setSelectedPage] = useState(RetreatPageOptions.WeekSelection);
   const [week, setWeek] = useState();
   const [roomType, setRoomType] = useState(null);
   const [craftFairTable, setCraftFairTable] = useState(false);
   const [groupJoined, setGroupJoined] = useState();
   const [isInGroup, setIsInGroup] = useState(false);
   const [reservationInfo, setReservationInfo] = useState();
   const [signatureData, setSignatureData] = useState({ signature: '', signatureDate: null });
   const [promoCode, setPromoCode] = useState(INITIAL_PROMO_CODE);
   const [amountDue, setAmountDue] = useState(0.00);
   const [paymentAmount, setPaymentAmount] = useState(0.00);
   const [paymentType, setPaymentType] = useState('');
   // Set alongside amountDue once Stripe confirms, so the effect-triggered ProcessRegistration can
   // record which charge paid for the registration.
   const [paymentIntentId, setPaymentIntentId] = useState(null);

   const [personalInformation, setPersonalInformation] = useState({
      firstName: '',
      lastName: '',
      gender: '',
      strAddress: '',
      strAddress2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      ecName: '',
      ecRelationship: '',
      ecPhone: '',
      birthDate: '',
      spouseFirstName: '',
      spouseLastName: '',
      spouseBirthDate: '',
      sonNames: [{ firstName: '', lastName: '', birthDate: '' }]
   });

   const [medicalInformation, setMedicalInformation] = useState({
      insuranceCompany: '',
      insuranceId: '',
      groupPolicyNumber: '',
      policyHolderName: '',
      policyHolderBirthdate: null,
      hasDiabetes: 'off',
      hasHeartCondition: 'off',
      hasEpilepsy: 'off',
      hasEnvironmentAllergies: 'off',
      hasHeatExhaustion: 'off',
      hasThyroidProblem: 'off',
      hasLupus: 'off',
      hasHighBloodPressure: 'off',
      hasAsthma: 'off',
      drugAllergies: '',
      emergencyAction1: '',
      foodAllergies: '',
      emergencyAction2: '',
      otherAllergies: '',
      emergencyAction3: '',
      medicationName: '',
      medicationDose: '',
      tetanusShotDate: null
   });

   const retreatConfig = getRetreatConfig(week);
   const sonCount = getSonCount(personalInformation);
   const roomSurcharge = getRoomSurcharge(week, roomType);
   const sonSurcharge = getSonSurcharge(week, sonCount);

   // Retreats that offer no room choice still record one — Couples Retreat is deluxe-only, so it
   // stores 'deluxe' at no charge so the rooming list stays accurate.
   useEffect(() => {
      if (week) {
         setRoomType(getDefaultRoomType(week));
      }
   }, [week]);

   async function setAccountForCurrentGuest() {
      const accountResponse = await DataAccessHelper.getCurrentAccountInformation();
      setAccount(accountResponse.account);
   }

   async function validateAndProcessRegistration(paymentIntentId) {
      try {
         const response = await fetch('/api/verify-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentIntentId }),
         });

         if (!response.ok) return false;

         const data = await response.json();
         if (data.result.status === 'succeeded') {
            const pricePaid = InputValidation.shiftDecimalLeft(data.result.amount, 2);
            const eventPrice = getRetreatTotal(week, { roomType, sonCount });
            const effectivePrice = applyPromo(eventPrice, promoCode);
            const due = (effectivePrice - pricePaid).toFixed(2);
            setPaymentIntentId(paymentIntentId);
            setAmountDue(due);
            return true;
         }
         return false;
      } catch (err) {
         console.error('Error validating payment intent:', err);
      }
   }

   // Puts the finished registration in the cart instead of collecting payment here. The payload is
   // the same body /retreatregistration has always taken, stored verbatim and replayed at checkout.
   async function AddToCart() {
      try {
         const response = await fetch('/cart/add', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               flow: 'retreat',
               payload: {
                  accountId: account.account_id,
                  groupJoined: groupJoined ?? { group_id: null },
                  week,
                  // The disclaimer was signed now, so this is the date that belongs on the
                  // registration — not whenever payment happens to land.
                  signatureData: {
                     ...signatureData,
                     signatureDate: signatureData.signatureDate ?? new Date().toISOString(),
                  },
                  reservationInfo,
                  isInGroup,
                  personalInformation,
                  medicalInformation,
                  roomType,
                  craftFairTable,
               },
            })
         });

         const data = await response.json()
         if (!response.ok) {
            return data.error ?? 'Could not add this registration to your cart.'
         }

         refreshCount()
         navigate('/mycart')
         return ''
      } catch (error) {
         console.log(error)
         return 'Could not add this registration to your cart.'
      }
   }

   async function ProcessRegistration() {
      const registrationId = await AddRegistration();
      // Written even at $0 so every registration has a row in the ledger.
      if (registrationId) {
         await AddTransaction(registrationId);
      }
      navigate('/retreatpaymentsuccess', { state: { paymentAmount, promoCode } });
   }

   async function AddRegistration() {
      const accountId = account.account_id;
      try {
         const registrationRequest = await fetch('/retreatregistration', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               accountId,
               groupJoined: groupJoined ?? { group_id: null },
               promoCode,
               week,
               signatureData,
               reservationInfo,
               amountDue,
               paymentAmount,
               isInGroup,
               personalInformation,
               medicalInformation,
               roomType,
               craftFairTable,
               // The camp fee actually charged, including the room upgrade and any per-son fees.
               // Stored instead of the raw event fee so reports reconcile against amount_due.
               effectiveCampFee: getRetreatCampFee(week, { roomType, sonCount }),
            })
         });
         const result = await registrationRequest.json();
         return result.data.retreatRegistration[0].regist_id;
      } catch (error) {
         console.log(error);
      }
   }

   async function AddTransaction(registrationId) {
      try {
         await fetch('/addtransaction', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               transactionValue: paymentAmount,
               // Was sent as reservationId, which filed the payment against reservation_id instead
               // of the registration it belonged to.
               registId: registrationId,
               paymentType,
               paymentIntentId
            })
         });
      } catch (error) {
         console.log(error);
      }
   }

   useEffect(() => {
      setAccountForCurrentGuest();
   }, []);

   useEffect(() => {
      if (amountDue == null) return;
      ProcessRegistration();
   }, [amountDue]);

   // The Additional Details step has nothing to show for some retreats. Wire around it here rather
   // than letting the step redirect itself on mount, which made the Previous button on the page
   // after it appear dead.
   const skipDetails = !showsAdditionalDetails(week);
   const afterMedical = skipDetails ? RetreatPageOptions.JoinGroup : RetreatPageOptions.AdditionalDetails;
   const beforeJoinGroup = skipDetails ? RetreatPageOptions.MedicalInsurance : RetreatPageOptions.AdditionalDetails;

   return (
      <div className="retreatRegistrationContainer">
         {selectedPage === RetreatPageOptions.WeekSelection &&
            <RetreatWeekSelection
               nextPage={RetreatPageOptions.PersonalInfo}
               setSelectedPage={setSelectedPage}
               setWeekSelection={w => setWeek(w)}
            />
         }
         {selectedPage === RetreatPageOptions.PersonalInfo && retreatConfig.personalInfoForm === 'couples' &&
            <CouplesRetreatPersonalInfo
               personalInformation={personalInformation}
               setPersonalInformation={setPersonalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.WeekSelection}
               nextPage={RetreatPageOptions.MedicalInsurance}
            />
         }
         {selectedPage === RetreatPageOptions.PersonalInfo && retreatConfig.personalInfoForm === 'fatherSon' &&
            <FatherSonPersonalInfo
               week={week}
               personalInformation={personalInformation}
               setPersonalInformation={setPersonalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.WeekSelection}
               nextPage={RetreatPageOptions.MedicalInsurance}
            />
         }
         {selectedPage === RetreatPageOptions.PersonalInfo && retreatConfig.personalInfoForm === 'standard' &&
            <PersonalInfo
               week={week}
               personalInformation={personalInformation}
               setPersonalInformation={setPersonalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.WeekSelection}
               nextPage={RetreatPageOptions.MedicalInsurance}
            />
         }
         {selectedPage === RetreatPageOptions.MedicalInsurance &&
            <MedicalInsuranceInformation
               medicalInformation={medicalInformation}
               setMedicalInformation={setMedicalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.PersonalInfo}
               nextPage={afterMedical}
            />
         }
         {selectedPage === RetreatPageOptions.AdditionalDetails &&
            <RegistrationAdditionalDetails
               week={week}
               roomType={roomType}
               setRoomType={setRoomType}
               craftFairTable={craftFairTable}
               setCraftFairTable={setCraftFairTable}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.MedicalInsurance}
               nextPage={RetreatPageOptions.JoinGroup}
            />
         }
         {selectedPage === RetreatPageOptions.JoinGroup &&
            <RetreatJoinGroup
               groupJoined={groupJoined}
               setGroupJoined={setGroupJoined}
               setIsInGroup={setIsInGroup}
               week={week}
               setSelectedPage={setSelectedPage}
               previousPage={beforeJoinGroup}
               nextPage={RetreatPageOptions.Disclaimer}
               reservationInfo={reservationInfo}
               setReservationInfo={setReservationInfo}
               setPromoCode={setPromoCode}
            />
         }
         {selectedPage === RetreatPageOptions.Disclaimer &&
            <Disclaimer
               week={week}
               signatureData={signatureData}
               setSignatureData={setSignatureData}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               nextPage={RetreatPageOptions.Review}
               previousPage={RetreatPageOptions.JoinGroup}
            />
         }
         {selectedPage === RetreatPageOptions.Review &&
            <RetreatRegistrationReview
               week={week}
               roomType={roomType}
               sonNames={personalInformation.sonNames}
               sonCount={sonCount}
               craftFairTable={craftFairTable}
               groupJoined={groupJoined}
               setGroupJoined={setGroupJoined}
               promoCode={promoCode}
               signatureData={signatureData}
               setSelectedPage={setSelectedPage}
               ProcessRegistration={ProcessRegistration}
               AddToCart={AddToCart}
            />
         }
         {selectedPage === RetreatPageOptions.Payment &&
            <IndividualRegistrationPayment
               account={account}
               amountDue={amountDue}
               setAmountDue={setAmountDue}
               paymentAmount={paymentAmount}
               setPaymentAmount={setPaymentAmount}
               week={week}
               roomSurcharge={roomSurcharge}
               sonSurcharge={sonSurcharge}
               sonCount={sonCount}
               paymentType={paymentType}
               setPaymentType={setPaymentType}
               promoCode={promoCode}
               setPromoCode={setPromoCode}
               groupJoined={groupJoined}
               ProcessRegistration={ProcessRegistration}
               validateAndProcessRegistration={validateAndProcessRegistration}
               setSelectedPage={setSelectedPage}
               previousPage={RetreatPageOptions.Review}
            />
         }
      </div>
   )
}

export default RetreatRegistration;
